import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AutoComplete, Button, Input, message, Select, Skeleton, Space } from 'antd';
import { CheckCircleOutlined, DeleteOutlined, EditOutlined, InfoCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import { useUserLevel } from 'reducers';
import {
	UserLevel,
	getReadablePriceType,
	PriceType,
	PaymentType,
	getReadablePaymentType,
	TaskType,
	OrderType,
	BDO,
} from 'types';
import { AsyncButton, DateSelectModal, PhoneLink, TaskCreationModal } from 'components';
import { ClientInfo } from '../components/client-info';
import { Page, PageSection, WarningModal } from '../../../../components.js';
import { Formatting } from '../../../../utils';
import { useBigBagOrderDetails } from './use-big-bag-order-details';

import '../delivery/styles/delivery_details.scss';
import styled from 'styled-components';
import { ClaimingModal } from './components/claiming-modal';
import { BdoCardPanel } from '../components/bdo/bdo-card-panel';
import { BdoStatementForm } from '../components/bdo/bdo-statement-form';
import { getBdoStatementDefaults } from '../components/bdo/bdo-statement-defaults';
import { BdoTypeField } from '../components/bdo/bdo-type-field';

const wasteTypes = ['Mieszany', 'Gruz', 'Drewno', 'Styropian', 'Papa'];

const PaymentTable = styled.table`
	width: 100%;
	border-collapse: collapse;
`;

const TableHeader = styled.th`
	text-align: ${(props) => props.align || 'left'};
	padding: 8px;
	border-bottom: 1px solid #ddd;
`;

const TableCell = styled.td`
	padding: 8px;
	border-bottom: 1px solid #eee;
	text-align: ${(props) => props.align || 'left'};
`;

const FooterCell = styled(TableCell)`
	font-weight: bold;
	border-bottom: none;
`;

export const BigBagOrderDetails: React.FC = () => {
	const navigate = useNavigate();
	const params = useParams();
	const id = Number.parseInt(params['id'] ?? '');
	const userLevel = useUserLevel();

	const [wasteType, setWasteType] = useState<string>();
	const [address, setAddress] = useState<string>();
	const [deliveryDateModal, setDeliveryDateModal] = useState(false);
	const [claimingDateModal, setClaimingDateModal] = useState(false);
	const [createDeliveryTaskModal, setCreateDeliveryTaskModal] = useState(false);
	const [createClaimingTaskModal, setCreateClaimingTaskModal] = useState(false);
	const [count, setCount] = useState<string>();
	const [price, setPrice] = useState<string>();
	const [priceType, setPriceType] = useState<PriceType>();
	const [wantedAdvance, setWantedAdvance] = useState<string>();
	const [paymentType, setPaymentType] = useState<PaymentType>();
	const [removalModal, setRemovalModal] = useState(false);
	const [removing, setRemoving] = useState(false);

	const {
		order,
		fetchOrder,
		client,
		claimingAssignee,
		deliveryAssignee,
		users,
		priceChanges,
		editOrder,
		deleteOrder,
		editClaimingTask,
		editDeliveryTask,
		deleteClaimingTask,
		markAsPaid,
		createTask,
	} = useBigBagOrderDetails(id);
	if (userLevel === undefined || !order || !client) {
		return <Skeleton active />;
	}

	const canEdit = userLevel > UserLevel.driver && !order.claimingTask?.doneAt;
	const isDone = order.onPlace ? !!order.deliveryTask?.doneAt : !!order.claimingTask?.doneAt;
	const alreadyPaidSum = order.payments.reduce((sum, payment) => sum + payment.amount, 0);
	const isFullyPaid = alreadyPaidSum >= order.grossPrice;

	const actions = [
		order && !isFullyPaid && !order.deliveryTask?.takenAt && userLevel >= UserLevel.superAdmin && (
			<Button
				key={0}
				size="large"
				type="primary"
				danger
				icon={<DeleteOutlined />}
				onClick={() => setRemovalModal(true)}
			>
				Usuń zamówienie
			</Button>
		),
	];

	const _deleteOrder = async () => {
		if (!order) return;

		setRemoving(true);
		const success = await deleteOrder();
		setRemoving(false);

		if (!success) {
			return;
		}

		navigate('/panel/orders', { replace: true });
	};

	const _canSavePrice = () => {
		const realPrice = order.priceType == PriceType.gross ? order.grossPrice : order.netPrice;
		if ((price ?? '').trim() != '' && price != `${realPrice}`) {
			return true;
		}

		if (priceType !== undefined && priceType !== order.priceType) {
			return true;
		}

		return false;
	};

	const _changeClaiming = async ({
		date,
		time,
		itemsToClaim,
		price,
	}: {
		date: moment.Moment;
		time: moment.Moment | undefined;
		itemsToClaim: number;
		price: number;
	}) => {
		if (order.claimingTask) {
			return;
		}

		const taskDate = date
			.clone()
			.hours(time?.hours() ?? 0)
			.minutes(time?.minutes() ?? 0)
			.seconds(0)
			.milliseconds(0);

		await createTask({
			orderId: order.id,
			orderType: OrderType.loose,
			type: TaskType.claiming,
			date: taskDate,
			itemsToClaim,
			newPrice: price,
		});

		setCreateClaimingTaskModal(false);
	};

	return (
		<Page title="Szczegóły zamówienia" actions={actions}>
			<PageSection title="Klient">
				<div className="delivery-details">
					<ClientInfo client={client} name />

					<PageSection title="Informacje">
						<Space size={25}>
							<div className="flex flex-dir">
								<label>BDO</label>
								<span className="flex items-center gap-4">
									<BdoTypeField
										orderType={OrderType.loose}
										orderId={order.id}
										value={order.bdo}
										editable={userLevel > UserLevel.driver && !isDone}
										cardConfirmedAt={order.bdoCardConfirmedAt ?? null}
										onChange={(bdo) => editOrder({ bdo })}
									/>

									<div>
										{order.bdo === BDO.producing ? (
											<BdoStatementForm
												orderType={OrderType.loose}
												orderId={order.id}
												canSign={true}
												readOnly={false}
												defaults={getBdoStatementDefaults(client, order)}
												onChanged={fetchOrder}
											/>
										) : null}

										{order.bdo === BDO.BDO ? (
											<BdoCardPanel
												orderType={OrderType.loose}
												orderId={order.id}
												confirmedAt={order.bdoCardConfirmedAt ?? null}
												confirmedBy={
													order.bdoCardConfirmedBy
														? users?.find((u) => u.id === order.bdoCardConfirmedBy) ?? null
														: null
												}
												onChanged={fetchOrder}
											/>
										) : null}
									</div>
								</span>
							</div>

							<div className="flex flex-dir">
								<label>Rodzaj odpadów</label>
								{canEdit ? (
									<div className="flex waste-type">
										<AutoComplete
											size="large"
											options={wasteTypes.map((type) => ({ value: type }))}
											filterOption={(inputValue, option) =>
												option?.value.toLocaleLowerCase().includes(inputValue.toLocaleLowerCase()) ?? false
											}
											value={wasteType ?? order.wasteType}
											onSelect={(type) => setWasteType(type)}
											onChange={(type) => setWasteType(type)}
										/>
										<AsyncButton
											icon={<CheckCircleOutlined />}
											onClick={async () => {
												if (!wasteType) return;
												await editOrder({ wasteType });
											}}
											disabled={!wasteType || wasteType === order.wasteType}
										>
											Zmień
										</AsyncButton>
									</div>
								) : (
									<span>{order.wasteType}</span>
								)}
							</div>
						</Space>

						<Space size={25}>
							<div className="flex flex-dir">
								<label>Adres</label>
								{canEdit ? (
									<div className="flex address">
										<Input size="large" value={address ?? order.address} onChange={(e) => setAddress(e.target.value)} />
										<AsyncButton
											icon={<CheckCircleOutlined />}
											onClick={async () => {
												if (!address) return;
												await editOrder({ address });
											}}
											disabled={!address || address === order.address}
										>
											Zmień
										</AsyncButton>
									</div>
								) : (
									<span>{order.address}</span>
								)}
							</div>
						</Space>

						<Space size={25}>
							<div className="flex flex-dir">
								<label>Numer kontaktowy</label>
								<PhoneLink phone={order.contact} />
							</div>
						</Space>

						<Space size={25}>
							<div className="flex flex-dir">
								<label>Uwagi</label>
								<span>{order.comments ?? '-'}</span>
							</div>
						</Space>
					</PageSection>

					<PageSection title="Zapotrzebowanie">
						<Space size={25}>
							<div className="flex flex-dir">
								<label>{order.onPlace ? 'Termin realizacji (na miejscu)' : 'Termin podstawienia'}</label>
								<span className="flex date">
									{order.deliveryTask?.date
										? Formatting.epochToDate(order.deliveryTask?.date, moment(order.deliveryTask?.date).hours() === 0)
										: 'Brak terminu podstawienia'}

									{!order.deliveryTask?.takenAt && (
										<Button
											type="primary"
											size="large"
											icon={<EditOutlined />}
											onClick={() => {
												if (!order.deliveryTask) {
													setCreateDeliveryTaskModal(true);
												}
												{
													setDeliveryDateModal(true);
												}
											}}
										>
											Zmień
										</Button>
									)}

									{order.deliveryTask && (
										<Button
											type="primary"
											size="large"
											icon={<InfoCircleOutlined />}
											onClick={() => navigate(`/panel/v2/tasks/${order.deliveryTask!.id}`)}
										>
											Szczegóły zadania
										</Button>
									)}
								</span>
							</div>
						</Space>

						{!order.onPlace && (
							<Space size={25}>
								<div className="flex flex-dir">
									<label>Termin odbioru</label>
									<span className="flex gap-2">
										{order.claimingTask?.date
											? Formatting.epochToDate(order.claimingTask?.date, moment(order.claimingTask?.date).hours() === 0)
											: 'Brak terminu odbioru'}

										{order.claimingTask && !order.claimingTask?.takenAt && (
											<AsyncButton danger icon={<DeleteOutlined />} onClick={deleteClaimingTask}>
												Usuń
											</AsyncButton>
										)}

										{!order.claimingTask?.takenAt && (
											<Button
												type="primary"
												size="large"
												icon={<EditOutlined />}
												onClick={() => {
													if (!order.claimingTask) {
														setCreateClaimingTaskModal(true);
													} else {
														setClaimingDateModal(true);
													}
												}}
											>
												Zmień
											</Button>
										)}

										{order.claimingTask && (
											<Button
												type="primary"
												size="large"
												icon={<InfoCircleOutlined />}
												onClick={() => navigate(`/panel/v2/tasks/${order.claimingTask!.id}`)}
											>
												Szczegóły zadania
											</Button>
										)}
									</span>
								</div>
							</Space>
						)}

						<Space size={25}>
							<div className="flex flex-dir">
								<label>Ilość worków</label>
								<div className="flex">
									<Input
										size="large"
										type="number"
										value={count ?? order.count}
										min={1}
										max={255}
										disabled={!canEdit}
										onChange={(e) => setCount(e.target.value)}
									/>
									<div style={{ width: '10px' }}></div>
									<AsyncButton
										icon={<CheckCircleOutlined />}
										onClick={async () => {
											if (!count) {
												return;
											}

											const countParsed = Number.parseInt(count);
											if (isNaN(countParsed)) {
												message.error('Niepoprawna ilość worków');
												return;
											}

											if (countParsed < 1 || countParsed > 255) {
												message.error('Ilość worków musi być z zakresu 1 - 255');
												return;
											}

											await editOrder({ count: countParsed });
										}}
										disabled={!canEdit || !count || Number.parseInt(count) == order.count}
									>
										Zmień
									</AsyncButton>
								</div>
							</div>
						</Space>
					</PageSection>

					<PageSection title="Historia">
						{order.createdAt && (
							<Space size={25}>
								<div className="flex flex-dir">
									<label>Zamówienie stworzone</label>
									<span>{Formatting.epochToDate(order.createdAt)}</span>
								</div>
							</Space>
						)}
						{order.deliveryTask?.takenAt && deliveryAssignee && (
							<Space size={25}>
								<div className="flex flex-dir">
									<label>Zgłoszone do podstawienia</label>
									<span>{Formatting.epochToDate(order.deliveryTask.takenAt)}</span>
								</div>
								<div className="flex flex-dir">
									<label>Zgłoszone do podstawienia przez</label>
									<span>
										{deliveryAssignee.name} {deliveryAssignee.surname}
									</span>
								</div>
							</Space>
						)}
						{order.deliveryTask?.doneAt && deliveryAssignee && (
							<Space size={25}>
								<div className="flex flex-dir">
									<label>Podstawione</label>
									<span>{Formatting.epochToDate(order.deliveryTask.doneAt)}</span>
								</div>
								<div className="flex flex-dir">
									<label>Podstawione przez</label>
									<span>
										{deliveryAssignee.name} {deliveryAssignee.surname}
									</span>
								</div>
							</Space>
						)}
						{order.claimingTask?.takenAt && claimingAssignee && (
							<Space size={25}>
								<div className="flex flex-dir">
									<label>Zgłoszone do odbioru</label>
									<span>{Formatting.epochToDate(order.claimingTask.takenAt)}</span>
								</div>
								<div className="flex flex-dir">
									<label>Zgłoszone do odbioru przez</label>
									<span>
										{claimingAssignee.name} {claimingAssignee.surname}
									</span>
								</div>
							</Space>
						)}
						{order.claimingTask?.doneAt && claimingAssignee && (
							<Space size={25}>
								<div className="flex flex-dir">
									<label>Odebrane</label>
									<span>{Formatting.epochToDate(order.claimingTask.doneAt)}</span>
								</div>
								<div className="flex flex-dir">
									<label>Odebrane przez</label>
									<span>
										{claimingAssignee.name} {claimingAssignee.surname}
									</span>
								</div>
							</Space>
						)}
					</PageSection>

					<PageSection title="Finanse">
						<div className="flex flex-col md:flex-row gap-4 w-full">
							<div className="flex flex-dir w-full gap-4">
								<div className="flex flex-dir">
									<label>
										Cena (<span className="price-type">{getReadablePriceType(priceType ?? order.priceType)}</span>)
									</label>
									<span>
										{Formatting.money(
											(priceType ?? order.priceType) == PriceType.gross ? order.grossPrice : order.netPrice
										)}
									</span>
									{userLevel >= UserLevel.superAdmin && !isFullyPaid && (
										<div className="flex flex-row gap-4 w-full">
											<div className="flex w-full gap-1">
												<Input
													size="large"
													type="text"
													value={
														price ??
														((priceType ?? order.priceType) == PriceType.gross ? order.grossPrice : order.netPrice)
													}
													onChange={(e) => setPrice(e.target.value)}
												/>
												<span className="pln flex flex-dir flex-jc flex-ac">PLN</span>
											</div>
											<Select
												size="large"
												value={priceType ?? order.priceType}
												onChange={(p) => setPriceType(p)}
												className="w-full"
											>
												<Select.Option key="0" value={PriceType.net}>
													Netto
												</Select.Option>
												<Select.Option key="1" value={PriceType.gross}>
													Brutto
												</Select.Option>
											</Select>
											<AsyncButton
												icon={<CheckCircleOutlined />}
												onClick={async () => {
													const newPrice = price ? Number.parseFloat(price) : undefined;
													if (newPrice && isNaN(newPrice)) {
														message.error('Niepoprawna cena');
														return;
													}

													const newPriceType = priceType;
													if (!newPrice && !newPriceType) {
														return;
													}

													await editOrder({
														price: newPrice,
														priceType: newPriceType,
													});

													setPrice(undefined);
													setPriceType(undefined);
												}}
												disabled={!_canSavePrice()}
											>
												Zapisz zmiany
											</AsyncButton>
										</div>
									)}
								</div>
								<div className="flex flex-dir">
									<label>Zaliczka</label>
									<span>
										{wantedAdvance ?? order.wantedAdvance
											? Formatting.money(wantedAdvance ?? order.wantedAdvance)
											: '-'}
									</span>
									{userLevel >= UserLevel.superAdmin && !isFullyPaid && (
										<div className="flex flex-row gap-4 w-full">
											<div className="flex w-full gap-1">
												<Input
													size="large"
													type="text"
													value={wantedAdvance ?? order.wantedAdvance ?? ''}
													onChange={(e) => setWantedAdvance(e.target.value)}
												/>
												<span className="pln flex flex-dir flex-jc flex-ac">PLN</span>
											</div>
											<AsyncButton
												icon={<CheckCircleOutlined />}
												onClick={async () => {
													const wantedAdvanceRaw = wantedAdvance?.trim() ?? null;
													if (!wantedAdvanceRaw) {
														await editOrder({ wantedAdvance: null });
														return;
													}

													const a = Number.parseFloat(wantedAdvanceRaw);
													if (isNaN(a)) {
														message.error('Niepoprawna wartość zaliczki');
														return;
													}

													await editOrder({
														wantedAdvance: a,
													});
												}}
												disabled={
													wantedAdvance === undefined ||
													wantedAdvance === order.wantedAdvance?.toString() ||
													(!wantedAdvance && order.wantedAdvance === null)
												}
											>
												Zmień zaliczkę
											</AsyncButton>
										</div>
									)}
								</div>
								<div className="flex flex-dir">
									<label>Typ płatności</label>
									<span>{getReadablePaymentType(order.wantedPaymentType)}</span>
									{userLevel >= UserLevel.superAdmin && !order.claimingTask?.doneAt && (
										<div className="flex flex-row gap-4 payment-type">
											<Select
												size="large"
												value={paymentType ?? order.wantedPaymentType}
												onChange={(p) => setPaymentType(p)}
												className="w-full"
											>
												<Select.Option key="0" value={PaymentType.transfer}>
													Przelew
												</Select.Option>
												<Select.Option key="1" value={PaymentType.cash}>
													Gotówka
												</Select.Option>
											</Select>
											<AsyncButton
												icon={<CheckCircleOutlined />}
												onClick={async () => {
													if (!paymentType) return;
													await editOrder({
														wantedPaymentType: paymentType,
													});
												}}
												disabled={!paymentType || paymentType === order.wantedPaymentType}
											>
												Zmień
											</AsyncButton>
										</div>
									)}
								</div>
								{userLevel >= UserLevel.superAdmin && !isFullyPaid && (
									<AsyncButton
										icon={<CheckCircleOutlined />}
										onClick={() => markAsPaid()}
										disabled={alreadyPaidSum >= order.grossPrice}
									>
										Oznacz jako zapłacone
									</AsyncButton>
								)}
							</div>
							<div className="flex flex-dir w-full">
								<label>
									<strong>Historia płatności</strong>
								</label>
								<div className="mb-4">
									{order.payments.length > 0 ? (
										<PaymentTable>
											<thead>
												<tr>
													<TableHeader>Data</TableHeader>
													<TableHeader align="center">Przez</TableHeader>
													<TableHeader align="right">Kwota</TableHeader>
												</tr>
											</thead>
											<tbody>
												{order.payments.map((payment, index) => (
													<tr key={index}>
														<TableCell>{Formatting.epochToDate(payment.date)}</TableCell>
														<TableCell align="center">
															{(() => {
																const user = users?.find((user) => user.id === payment.createdBy);
																return user ? `${user.name} ${user.surname}` : '-';
															})()}
														</TableCell>
														<TableCell align="right">{Formatting.money(payment.amount)}</TableCell>
													</tr>
												))}
											</tbody>
											<tfoot>
												<tr>
													<FooterCell>Razem</FooterCell>
													<FooterCell></FooterCell>
													<FooterCell align="right">{Formatting.money(alreadyPaidSum)}</FooterCell>
												</tr>
											</tfoot>
										</PaymentTable>
									) : (
										<p>Brak zarejestrowanych transakcji</p>
									)}
								</div>
								<label>
									<strong>Historia zmian ceny</strong>
								</label>
								<div className="mb-4">
									{priceChanges && priceChanges.length > 0 ? (
										<PaymentTable>
											<thead>
												<tr>
													<TableHeader>Data</TableHeader>
													<TableHeader align="center">Przez</TableHeader>
													<TableHeader align="right">Stara cena</TableHeader>
													<TableHeader align="right">Nowa cena</TableHeader>
												</tr>
											</thead>
											<tbody>
												{priceChanges.map((change) => {
													const user = users?.find((u) => u.id === change.createdBy);
													const oldPrice = Formatting.money(
														change.oldPriceType === PriceType.gross ? change.oldGrossPrice : change.oldNetPrice
													);
													const newPrice = Formatting.money(
														change.newPriceType === PriceType.gross ? change.newGrossPrice : change.newNetPrice
													);
													const oldPriceType = getReadablePriceType(change.oldPriceType);
													const newPriceType = getReadablePriceType(change.newPriceType);

													return (
														<tr key={change.id}>
															<TableCell>{Formatting.epochToDate(change.createdAt)}</TableCell>
															<TableCell align="center">{user ? `${user.name} ${user.surname}` : '-'}</TableCell>
															<TableCell align="right">
																{oldPrice} <span className="price-type">({oldPriceType})</span>
															</TableCell>
															<TableCell align="right">
																{newPrice} <span className="price-type">({newPriceType})</span>
															</TableCell>
														</tr>
													);
												})}
											</tbody>
										</PaymentTable>
									) : (
										<p>Brak historii zmian ceny</p>
									)}
								</div>
							</div>
						</div>

						<Space size={25}>
							{/* <div className="flex flex-dir">
									<label>Faktura</label>
									<Input size="large" type="text" value={order.invoice} onChange={invoiceChanged} />
								</div>
								<div className="flex flex-dir flex-ac">
									<label>&nbsp;</label>
									<div>
										<Button
											type="primary"
											size="large"
											icon={<AuditOutlined />}
											onClick={changeInvoice}
											loading={loading === 'invoice'}
										>
											Wystaw fakturę
										</Button>
									</div>
								</div> */}
						</Space>
					</PageSection>
				</div>
				<DateSelectModal
					visible={deliveryDateModal}
					onCancel={() => setDeliveryDateModal(false)}
					onOk={async (deliveryDate) => {
						const success = await editDeliveryTask({ date: deliveryDate });
						if (!success) {
							throw new Error('Nie udało się edytować zamówienia');
						}

						setDeliveryDateModal(false);
					}}
				/>
				<ClaimingModal
					visible={createClaimingTaskModal}
					order={order}
					onOk={_changeClaiming}
					onCancel={() => setCreateClaimingTaskModal(false)}
				/>
				<DateSelectModal
					visible={claimingDateModal}
					onCancel={() => setClaimingDateModal(false)}
					onOk={async (claimingDate) => {
						const success = await editClaimingTask({ date: claimingDate });
						if (!success) {
							throw new Error('Nie udało się edytować zamówienia');
						}

						setClaimingDateModal(false);
					}}
				/>
				<WarningModal
					visible={removalModal}
					onCancel={() => setRemovalModal(false)}
					onOk={() => _deleteOrder()}
					loading={removing}
				>
					<h2>Czy na pewno chcesz usunąć to zamówienie?</h2>
				</WarningModal>
				<TaskCreationModal
					visible={createDeliveryTaskModal}
					orderId={id}
					type={TaskType.delivery}
					onCancel={() => setCreateDeliveryTaskModal(false)}
					onOk={async (date) => {
						const success = await createTask({
							date,
							type: TaskType.delivery,
							orderId: order.id,
							orderType: OrderType.loose,
						});
						if (!success) {
							throw new Error('Nie udało się utworzyć zadania');
						}

						setCreateDeliveryTaskModal(false);
					}}
				/>
			</PageSection>
		</Page>
	);
};
